/**
 * Vuex module for video conferences.
 */
import debug from 'debug'
import API from '@/api'
import events from 'Store/events'
import Room from 'Store/class/video_conference'

const dbg = debug('store:action:videoconf:')

const actions = {
  init: function ({ commit }) {
    dbg('Init application: video conferences')
    events.mask()
    return API.getVideoConferences(0, state.pagination.perPage, state.excludeDynamic).then(response => {
      const resp = response.data
      if (!resp.success) {
        dbg('Video conferences get response fail: %o', response)
        return
      }
      commit('RESET')
      for (const confer of resp.data) {
        commit('ADD_CONFER', confer)
      }
      commit('INIT_PAGINATION', resp.total)
    }).catch(err => {
      dbg(err)
      const msg = API.getException(err) ||
        'Failed to get video conferences from API.'
      events.pubError(msg)
    }).finally(() => {
      events.unmask()
    })
  },

  pageLoad: function ({ commit, state }, page) {
    return new Promise((resolve, reject) => {
      const start = (page - 1) * state.pagination.perPage
      return API.getVideoConferences(start, state.pagination.perPage).then(response => {
        const resp = response.data
        commit('RESET')

        for (const confer of resp.data) {
          commit('ADD_CONFER', confer)
        }

        commit('SET_PAGE', { page: page, start: start })
        resolve()
      }).catch(err => {
        reject(err.response.data)
      })
    })
  },

  setActiveRoom: function ({ commit }, room) {
    commit('SET_ACTIVE_ROOM', room)
  },

  destroyActiveRoom: function ({ commit }) {
    commit('DESTROY_ACTIVE_ROOM')
  },

  setExcludeDynamic: function ({ commit }) {
    commit('EXCLUDE_DYNAMIC')
    this.dispatch('videoconfer/init')
  },

  create: function ({ commit }, videoConference) {
    if (process.env.NODE_ENV === 'test') {
      return true
    }
    commit('CREATE', videoConference)
  },

  update: function ({ commit }, videoConference) {
    if (process.env.NODE_ENV === 'test') {
      return true
    }
    commit('UPDATE', videoConference)
  },

  destroy: function ({ commit }, videoConference) {
    if (process.env.NODE_ENV === 'test') {
      return true
    }
    commit('DESTROY', videoConference)
  }
}

const mutations = {
  RESET (state) {
    state.rooms.splice(0)
  },

  ADD_CONFER (state, data) {
    const room = new Room(data)
    if (room.isValid) {
      state.rooms.push(room)
    } else {
      dbg('Failed to add room: %o', data)
    }
  },

  INIT_PAGINATION (state, total) {
    state.pagination.total = total
    state.pagination.pages = Math.ceil(total / state.pagination.perPage)
    state.pagination.curPage = 1
    state.pagination.start = 0
    state.pagination.limit = state.pagination.perPage
  },

  SET_PAGE (state, { page, start }) {
    state.pagination.curPage = page
    state.pagination.start = start
  },

  SET_ACTIVE_ROOM (state, room) {
    state.activeRoom = room
  },

  DESTROY_ACTIVE_ROOM (state) {
    state.activeRoom = null
  },

  EXCLUDE_DYNAMIC (state) {
    state.excludeDynamic = !state.excludeDynamic
  },

  CREATE (state, videoConference) {
    API.createVideoConference(videoConference).then(resp => {
      this.dispatch('videoconfer/init')
    }).catch(err => {
      dbg(err.response)
      events.pubError(API.getException(err) || 'Failed to create video conference')
    })
  },

  UPDATE (state, videoConference) {
    API.updateVideoConference(videoConference).then(resp => {
      this.dispatch('videoconfer/init')
    }).catch(err => {
      dbg(err.response)
      events.pubError(API.getException(err) || 'Failed to update video conference')
    })
  },

  DESTROY (state, videoConference) {
    API.deleteVideoConference(videoConference).then(resp => {
      this.dispatch('videoconfer/init')
    }).catch(err => {
      dbg(err.response)
      events.pubError(API.getException(err) || 'Failed to destroy video conference')
    })
  }
}

const state = {
  activeRoom: null,
  rooms: [],
  excludeDynamic: true,
  pagination: {
    perPage: 9,
    pages: 0,
    curPage: 0,
    start: 0,
    total: 0
  }
}

const getters = {
  activeRoom: state => state.activeRoom,
  pagination: state => state.pagination,
  total: state => state.pagination?.total,
  excludeDynamic: state => state.excludeDynamic,
  rooms: state => state.rooms
}

export default {
  namespaced: true,
  state,
  getters,
  actions,
  mutations
}
